import React, { useCallback, useMemo } from 'react';

import { Grid, makeStyles } from '@material-ui/core';

import PackagePlansCardPricing from './PackagePlansCardPricing';
import PackagePlansContact from './PackagePlansContact';
import { orderTrackingStatuses } from '../../models/Order';
import { WorkspaceProductType } from '../../models/Workspace';
import PaymentUtils from '../../utils/payment.utils';

const useStyles = makeStyles((theme) => {
  return {
    gridContainer: {
      padding: '0px 30px',
    },
    gridItem: {
      borderBottom: '1px solid #D9D9D9',
      borderRight: 'none',
      padding: '50px 0px',
    },
    gridItemLast: {
      padding: '50px 0px 10px',
    },
    gridItemRight: {},
    packagePlanContainer: {
      paddingTop: '16px',
    },
    [theme.breakpoints.up('md')]: {
      gridItem: {
        borderBottom: 'none',
        borderRight: '1px solid #D9D9D9',
        padding: '20px 0px',
      },
      gridItemLast: {
        padding: '20px 0px',
      },
      gridItemRight: { borderRight: 'none' },
    },
    [theme.breakpoints.up('lg')]: {
      gridItemRight: { borderRight: '1px solid #D9D9D9' },
    },
  };
});

export default ({ onSelect, userDetails, selectedRecord, discountData }) => {
  const classes = useStyles();
  const isWhiteGlove = useMemo(
    () => [userDetails?.workspace?.productType, userDetails?.referenceWorkspace?.productType].includes(WorkspaceProductType.whiteGlove.value),
    [userDetails]
  );

  const handleSelect = useCallback(
    (plans) => {
      if (!onSelect || typeof onSelect !== 'function') return;
      onSelect(plans);
    },
    [onSelect]
  );

  const packagesSetup = useMemo(() => {
    const { firstOrderStatus = '' } = userDetails || {};
    const { demo, registered } = orderTrackingStatuses;
    const packages = [
      {
        buttonText: 'Buy Now',
        contentType: 'pricing',
        data: PaymentUtils.PACKAGE_STANDARD_VIDEO_REPORT,
        handleMenuClick: () => handleSelect(PaymentUtils.PACKAGE_STANDARD_VIDEO_REPORT.plan),
        id: 'package_plans_video_standard',
      },
      {
        buttonText: 'Buy Now',
        contentType: 'pricing',
        data: PaymentUtils.PACKAGE_EXPRESS_VIDEO_REPORT,
        handleMenuClick: () => handleSelect(PaymentUtils.PACKAGE_EXPRESS_VIDEO_REPORT.plan),
        id: 'package_plans_video_express',
      },
    ];

    const shouldShowFreeAccount = (firstOrderStatus === demo.value || firstOrderStatus === registered.value) && !selectedRecord?.id;
    if (shouldShowFreeAccount) {
      packages.unshift({
        buttonText: 'Access Now',
        contentType: 'pricing',
        data: PaymentUtils.PACKAGE_FREE_ACCOUNT,
        handleMenuClick: () => handleSelect('demo'),
        id: 'package_plans_video_free',
      });
    } else {
      packages.push({
        contentType: 'contact',
        id: 'package_plans_video_contact',
      });
    }

    return packages;
  }, [userDetails, selectedRecord?.id, handleSelect]);

  const packageMaxFeatureList = useMemo(() => {
    let maxFeatureList = 0;
    packagesSetup.forEach((data) => {
      const featureListLength = data?.data?.featureList?.length;
      if (featureListLength && featureListLength > maxFeatureList) {
        maxFeatureList = featureListLength;
      }
    });

    return maxFeatureList;
  }, [packagesSetup]);

  const renderPriceElements = (packages, maxFeatureList) => {
    const elements = packages.map((packagePlan, index) => {
      const classSetup = [];
      if (index === 0) classSetup.push(classes.gridItem);
      else if (index === 1) classSetup.push(classes.gridItem, classes.gridItemRight);
      else classSetup.push(classes.gridItemLast);

      if (packagePlan.contentType === 'contact') {
        return (
          <Grid key={packagePlan.id} className={classSetup.join(' ')} item xs={12} md={6} lg={3}>
            <div className={classes.packagePlanContainer}>
              <PackagePlansContact />
            </div>
          </Grid>
        );
      }

      let price = packagePlan?.data?.plan?.price || 0;

      if (discountData.percent_off) price *= (100 - discountData.percent_off) / 100;
      else if (isWhiteGlove) price = (price * 90) / 100;

      if (JSON.stringify(price).includes('.')) price = price.toFixed(2);

      return (
        <Grid key={packagePlan.id} className={classSetup.join(' ')} item xs={12} md={6} lg={3}>
          <PackagePlansCardPricing
            buttonText={packagePlan.buttonText}
            title={packagePlan.data.name}
            price={`$${price}`}
            priceDetail={packagePlan.data.priceDetail}
            description={packagePlan.data.description}
            featureList={packagePlan.data.featureList}
            maxFeatureList={maxFeatureList}
            onClickConfirm={() => packagePlan.handleMenuClick()}
          />
        </Grid>
      );
    });

    return elements;
  };

  return (
    <>
      <Grid className={classes.gridContainer} container alignContent="stretch" justify="center">
        {renderPriceElements(packagesSetup, packageMaxFeatureList)}
      </Grid>
    </>
  );
};
